import Navbar from "@/components/Layout/Navbar";
import Footer from "@/components/Layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Calendar, Clock, Lightbulb, BadgeDollarSign, CheckCircle2, ArrowRight } from "lucide-react";
import { Link } from "@/lib/router-compat";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import heroImage from "@/assets/blog/egypt-outsourcing-hero.jpg";
import teamImage from "@/assets/blog/team-collaboration.jpg";
import SEOHead from "@/components/SEOHead";
import { BASE_URL } from "@/lib/seo";
import { useTranslation } from "react-i18next";
import { useLanguageRouting } from "@/hooks/useLanguageRouting";
import AuthorBio, { AUTHORS } from "@/components/EEAT/AuthorBio";
import ArticleMeta from "@/components/EEAT/ArticleMeta";
import SourcesList from "@/components/EEAT/SourcesList";
import TrustBar from "@/components/EEAT/TrustBar";

const EgyptOutsourcingCosts = () => {
  const { t, i18n } = useTranslation(['blog', 'common']);
  const { getLocalizedLink } = useLanguageRouting();
  const currentLang = i18n.language;

  const faqItems = [
    { question: t('blog:egyptCosts.faq.q1.question'), answer: t('blog:egyptCosts.faq.q1.answer') },
    { question: t('blog:egyptCosts.faq.q2.question'), answer: t('blog:egyptCosts.faq.q2.answer') },
    { question: t('blog:egyptCosts.faq.q3.question'), answer: t('blog:egyptCosts.faq.q3.answer') },
    { question: t('blog:egyptCosts.faq.q4.question'), answer: t('blog:egyptCosts.faq.q4.answer') },
    { question: t('blog:egyptCosts.faq.q5.question'), answer: t('blog:egyptCosts.faq.q5.answer') },
    { question: t('blog:egyptCosts.faq.q6.question'), answer: t('blog:egyptCosts.faq.q6.answer') },
  ];

  const evolviceUrl = currentLang === 'de' ? 'https://www.evolvice.de' : 'https://www.evolvice.de/en';

  const salaryHeaders = t('blog:egyptCosts.sections.costBreakdown.salaryTable.headers', { returnObjects: true }) as string[];
  const salaryRows = t('blog:egyptCosts.sections.costBreakdown.salaryTable.rows', { returnObjects: true }) as string[][];
  const keyTakeawayItems = t('blog:egyptCosts.keyTakeaways.items', { returnObjects: true }) as string[];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        titleEn="Egypt Outsourcing Cuts Tech Costs 70% (2026)"
        titleDe="Ägypten-Outsourcing senkt Kosten um 70% (2026)"
        descriptionEn="Egypt developers from €9,000/year. Save 36–60% on software costs. 2026 salary data and strategies."
        descriptionDe="Ägyptische Entwickler ab 9.000 €/Jahr. 36–60% Kosten sparen. Gehaltsdaten und Strategien 2026."
        keywords="Egypt software outsourcing 2026, cost reduction, software development Egypt, IT outsourcing, nearshore Egypt, tech cost savings, Egypt developer salaries"
        path="/blog/egypt-outsourcing-costs"
        type="article"
        publishedTime="2024-01-20T00:00:00Z"
        modifiedTime="2026-04-19T00:00:00Z"
        author={AUTHORS["nearshore-research"].nameEn}
        article={{
          title: t('blog:egyptCosts.title'),
          description: t('blog:egyptCosts.intro'),
          url: `${BASE_URL}${currentLang === 'de' ? '' : '/en'}/blog/egypt-outsourcing-costs`,
          image: `${BASE_URL}${heroImage}`,
          datePublished: "2024-01-20",
          dateModified: "2026-04-19",
          author: AUTHORS["nearshore-research"].nameEn,
          authorJobTitle: AUTHORS["nearshore-research"].roleEn,
          authorUrl: AUTHORS["nearshore-research"].linkedinUrl,
          reviewer: AUTHORS["sherif-adel"].nameEn,
        }}
        breadcrumbs={[
          { name: 'Home', nameDE: 'Startseite', url: '/' },
          { name: 'Blog', nameDE: 'Blog', url: '/blog' },
          { name: 'Egypt Outsourcing Costs', nameDE: 'Ägypten Outsourcing Kosten', url: '/blog/egypt-outsourcing-costs' }
        ]}
        faqs={faqItems}
      />

      <Navbar />

      <main className="pt-24 pb-32">
        <div className="container mx-auto px-6">
          <div className="flex gap-10 max-w-7xl mx-auto">
            {/* Main Content */}
            <div className="flex-1 max-w-3xl mx-auto lg:mx-0">
              <Button variant="ghost" className="mb-10 group -ml-3" asChild>
                <Link to={getLocalizedLink('/blog')}>
                  <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                  {t('blog:backToBlog')}
                </Link>
              </Button>

              <article>
                <header className="mb-12">
                  <div className="flex items-center gap-4 text-xs uppercase tracking-[0.15em] text-muted-foreground mb-6 flex-wrap">
                    <span className="px-3 py-1 rounded-full border border-border/60 bg-background/60 text-foreground/80">
                      {t('blog:egyptCosts.category')}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{t('blog:egyptCosts.date')}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      <span>{t('blog:egyptCosts.readTime')} {t('blog:minRead')}</span>
                    </div>
                  </div>

                  <h1 className="text-4xl md:text-5xl font-semibold tracking-[-0.035em] text-balance mb-6 leading-tight">
                    {t('blog:egyptCosts.title')}
                  </h1>

                  <ArticleMeta publishedDate="2024-01-20" updatedDate="2026-04-19" />

                  <AuthorBio
                    authorId="nearshore-research"
                    reviewedByDe={AUTHORS["sherif-adel"].nameDe + " — " + AUTHORS["sherif-adel"].roleDe}
                    reviewedByEn={AUTHORS["sherif-adel"].nameEn + " — " + AUTHORS["sherif-adel"].roleEn}
                  />

                  <div className="rounded-2xl overflow-hidden mb-8 border border-border/60">
                    <img src={heroImage} alt="Egypt software outsourcing - developers working in Cairo tech hub" className="w-full h-auto" />
                  </div>

                  <p className="text-xl text-foreground/80 leading-relaxed">
                    {t('blog:egyptCosts.intro')}
                  </p>
                </header>

                {/* Key Takeaways */}
                <Card className="mb-16">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-5">
                      <BadgeDollarSign className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                      <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
                        {t('blog:egyptCosts.keyTakeaways.title')}
                      </h2>
                    </div>
                    <ul className="space-y-3">
                      {Array.isArray(keyTakeawayItems) && keyTakeawayItems.map((item, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle2 className="w-4 h-4 text-foreground/40 mt-1 flex-shrink-0" />
                          <span className="text-foreground/80 leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <div className="max-w-none">
                  {/* Cost Breakdown */}
                  <section className="mb-16">
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Analysis</div>
                    <h2 className="text-3xl font-semibold tracking-[-0.028em] mb-6">{t('blog:egyptCosts.sections.costBreakdown.title')}</h2>
                    <p className="text-foreground/80 leading-relaxed mb-6">{t('blog:egyptCosts.sections.costBreakdown.p1')}</p>

                    <h3 className="text-xl font-semibold tracking-[-0.02em] mb-3 mt-8">{t('blog:egyptCosts.sections.costBreakdown.comparison.title')}</h3>
                    <p className="text-foreground/80 leading-relaxed mb-8">{t('blog:egyptCosts.sections.costBreakdown.comparison.description')}</p>

                    {/* Expert Insight 1 */}
                    <Card className="mb-8">
                      <CardContent className="p-6 flex gap-4">
                        <Lightbulb className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" aria-hidden="true" />
                        <div>
                          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-2">Expert Insight</p>
                          <p className="text-foreground/80 italic leading-relaxed">{t('blog:egyptCosts.expertInsight1')}</p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Salary Comparison Table */}
                    <h3 className="text-xl font-semibold tracking-[-0.02em] mb-4">{t('blog:egyptCosts.sections.costBreakdown.salaryTable.title')}</h3>
                    <Card className="mb-8 overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            {Array.isArray(salaryHeaders) && salaryHeaders.map((header, i) => (
                              <TableHead key={i} className="text-xs uppercase tracking-[0.12em] text-muted-foreground font-medium">
                                {header}
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Array.isArray(salaryRows) && salaryRows.map((row, i) => (
                            <TableRow key={i}>
                              {row.map((cell, j) => (
                                <TableCell
                                  key={j}
                                  className={`text-sm ${j === 0 ? 'font-medium text-foreground' : 'text-muted-foreground'} ${j === 1 ? 'text-foreground font-semibold' : ''}`}
                                >
                                  {cell}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Card>

                    <h3 className="text-xl font-semibold tracking-[-0.02em] mb-3 mt-8">{t('blog:egyptCosts.sections.costBreakdown.roles.title')}</h3>
                    <p className="text-foreground/80 leading-relaxed mb-6">{t('blog:egyptCosts.sections.costBreakdown.roles.description')}</p>

                    <h3 className="text-xl font-semibold tracking-[-0.02em] mb-3 mt-8">{t('blog:egyptCosts.sections.costBreakdown.hidden.title')}</h3>
                    <p className="text-foreground/80 leading-relaxed mb-6">{t('blog:egyptCosts.sections.costBreakdown.hidden.description')}</p>

                    {/* Currency Note */}
                    <Card className="mb-6">
                      <CardContent className="p-5">
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          <span className="text-xs uppercase tracking-[0.15em] text-foreground/70 font-medium mr-2">FX Note</span>
                          {t('blog:egyptCosts.sections.costBreakdown.currencyNote')}
                        </p>
                      </CardContent>
                    </Card>
                  </section>

                  {/* Success Stories */}
                  <section className="mb-16 pt-12 border-t border-border/60">
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Case Studies</div>
                    <h2 className="text-3xl font-semibold tracking-[-0.028em] mb-6">{t('blog:egyptCosts.sections.successStories.title')}</h2>

                    <div className="rounded-2xl overflow-hidden mb-8 border border-border/60">
                      <img src={teamImage} alt="Professional software development team in Egypt" className="w-full h-auto" />
                    </div>

                    <p className="text-foreground/80 leading-relaxed mb-6">{t('blog:egyptCosts.sections.successStories.intro')}</p>

                    <h3 className="text-xl font-semibold tracking-[-0.02em] mb-3 mt-8">{t('blog:egyptCosts.sections.successStories.fintech.title')}</h3>
                    <p className="text-foreground/80 leading-relaxed mb-6">{t('blog:egyptCosts.sections.successStories.fintech.description')}</p>

                    {/* Expert Insight 2 */}
                    <Card className="mb-8">
                      <CardContent className="p-6 flex gap-4">
                        <Lightbulb className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" aria-hidden="true" />
                        <div>
                          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-2">Expert Insight</p>
                          <p className="text-foreground/80 italic leading-relaxed">{t('blog:egyptCosts.expertInsight2')}</p>
                        </div>
                      </CardContent>
                    </Card>

                    <h3 className="text-xl font-semibold tracking-[-0.02em] mb-3 mt-8">{t('blog:egyptCosts.sections.successStories.enterprise.title')}</h3>
                    <p className="text-foreground/80 leading-relaxed mb-6">{t('blog:egyptCosts.sections.successStories.enterprise.description')}</p>

                    <h3 className="text-xl font-semibold tracking-[-0.02em] mb-3 mt-8">{t('blog:egyptCosts.sections.successStories.startups.title')}</h3>
                    <Card className="mb-6">
                      <CardContent className="p-7">
                        <p className="text-foreground/80 leading-relaxed">{t('blog:egyptCosts.sections.successStories.startups.description')}</p>
                      </CardContent>
                    </Card>
                  </section>

                  {/* FAQ */}
                  <section className="mb-16 pt-12 border-t border-border/60">
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">FAQ</div>
                    <h2 className="text-3xl font-semibold tracking-[-0.028em] mb-6">{t('blog:egyptCosts.faq.title')}</h2>
                    <Accordion type="single" collapsible className="w-full">
                      {faqItems.map((faq, index) => (
                        <AccordionItem key={index} value={`item-${index}`} className="border-border/60">
                          <AccordionTrigger className="text-left text-base font-medium tracking-[-0.015em] hover:no-underline">
                            {faq.question}
                          </AccordionTrigger>
                          <AccordionContent className="text-muted-foreground leading-relaxed">
                            {faq.answer}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </section>

                  <SourcesList
                    sources={[
                      { publisher: "Stack Overflow", label: "Developer Survey 2024 — Salaries by region", url: "https://survey.stackoverflow.co/2024/work#salary-comp-total", year: 2024 },
                      { publisher: "ITIDA Egypt", label: "Egypt's IT Industry Snapshot", url: "https://itida.gov.eg/", year: 2024 },
                      { publisher: "Statista", label: "Average software developer salary by country", url: "https://www.statista.com/statistics/673765/worldwide-software-developer-salaries-by-country/", year: 2025 },
                      { publisher: "Central Bank of Egypt", label: "InstaPay statistics", url: "https://www.cbe.org.eg/", year: 2024 },
                      { publisher: "Dell Technologies", label: "Egypt Center of Excellence", url: "https://www.delltechnologies.com/", year: 2024 },
                      { publisher: "Y Combinator", label: "Egyptian startups in YC", url: "https://www.ycombinator.com/companies?regions=Egypt", year: 2024 },
                    ]}
                  />

                  <AuthorBio
                    authorId="nearshore-research"
                    reviewedByDe={AUTHORS["sherif-adel"].nameDe + " — " + AUTHORS["sherif-adel"].roleDe}
                    reviewedByEn={AUTHORS["sherif-adel"].nameEn + " — " + AUTHORS["sherif-adel"].roleEn}
                  />

                  <div className="my-12">
                    <TrustBar />
                  </div>

                  {/* Bottom CTA */}
                  <Card className="text-center">
                    <CardContent className="py-14 px-8">
                      <h2 className="text-3xl font-semibold tracking-[-0.028em] mb-4">{t('blog:nearshoreGuide.cta.title')}</h2>
                      <p className="text-muted-foreground mb-8 leading-relaxed max-w-xl mx-auto">{t('blog:nearshoreGuide.cta.subtitle')}</p>
                      <Button size="lg" asChild>
                        <a href={evolviceUrl} target="_blank" rel="noopener noreferrer">
                          {t('blog:nearshoreGuide.cta.button')}
                          <ArrowRight className="w-4 h-4" />
                        </a>
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </article>
            </div>

            {/* Sticky Sidebar */}
            <aside className="hidden lg:block w-72 flex-shrink-0">
              <div className="sticky top-32">
                <Card>
                  <CardContent className="p-6">
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Get in touch</div>
                    <h3 className="text-lg font-semibold tracking-[-0.02em] mb-2">{t('blog:egyptCosts.sidebar.title')}</h3>
                    <p className="text-sm text-muted-foreground mb-5 leading-relaxed">{t('blog:egyptCosts.sidebar.subtitle')}</p>
                    <Button className="w-full" asChild>
                      <a href={evolviceUrl} target="_blank" rel="noopener noreferrer">
                        {t('blog:egyptCosts.sidebar.button')}
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default EgyptOutsourcingCosts;
